import { useEffect, useMemo, useState } from 'react';
import { ResendConnection, ResendResult, bulkResendApi, resendSubmissionApi } from '../../api/forms';
import { formatFull } from './submissionColumns';

/** The status pill's semantic pair, by delivery status. */
const STATUS_PILL: Record<string, string> = {
  failed: 'danger', success: 'ok', pending: 'warn', skipped: 'neutral', never: 'neutral',
};
const STATUS_WORD: Record<string, string> = {
  failed: 'failed', success: 'delivered', pending: 'pending', skipped: 'skipped', never: 'never sent',
};

/**
 * Choosing where to send a submission again.
 *
 * A checklist rather than a button, because neither blanket option is right:
 * sending to everything duplicates the order at destinations that already have
 * it, and sending only to what failed makes a first send to a connection added
 * later impossible. The defaults do the common thing; the list makes the rest
 * possible.
 */
export default function ResendDialog({ submissionId, submissionRef, connections, editedAgo, bulk, onClose, onSent }: {
  submissionId: number;
  submissionRef: string;
  connections: ResendConnection[];
  /** set when the drawer has just saved an edit — the usual reason for re-sending */
  editedAgo?: string;
  /** many submissions at once, from the table's selection */
  bulk?: { formId: number; submissionIds: number[] };
  onClose: () => void;
  onSent: () => void;
}) {
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [dryRun, setDryRun] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<ResendResult[] | null>(null);
  const [queued, setQueued] = useState<number | null>(null);
  const [error, setError] = useState('');

  // Failed ones start ticked; the ones that already landed do not.
  useEffect(() => {
    setPicked(new Set(connections.filter((c) => c.preselect).map((c) => c.id)));
  }, [connections]);

  const chosen = useMemo(() => connections.filter((c) => picked.has(c.id)), [connections, picked]);

  async function send() {
    setSending(true); setError('');
    try {
      if (bulk) {
        // Answered immediately and worked through in the background, so the
        // outcomes are read from the rows afterwards rather than here.
        const r = await bulkResendApi(bulk.formId, bulk.submissionIds, [...picked]);
        setQueued(r.queued);
        onSent();
        return;
      }
      const r = await resendSubmissionApi(submissionId, [...picked], dryRun);
      setResults(r.results);
      // A dry run changed nothing, so there is nothing for the table to reload.
      if (!dryRun && r.results.some((x) => x.status === 'success')) onSent();
    } catch (e: any) { setError(e.message); }
    finally { setSending(false); }
  }

  return (
    <div className="sl-modal-scrim" onClick={onClose}>
      <div className="sl-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sl-pop-head">Re-send {submissionRef}</div>

        <div className="sl-pop-body" style={{ maxHeight: 'none', flex: 1 }}>
          {error && <div className="ad-err">{error}</div>}

          {queued !== null ? (
            <div className="sl-prose">
              <b>{queued} submission{queued === 1 ? '' : 's'} queued.</b> They are being sent in the background — the
              Deliveries column updates as each one finishes, so refresh in a moment to see where they got to.
            </div>
          ) : results ? (
            <>
              <p className="sl-prose" style={{ marginTop: 0 }}>
                {dryRun ? 'Nothing was sent. This is what would go out:' : 'Result of this send:'}
              </p>
              {results.map((r) => (
                <div className={`sl-conn ${r.status === 'failed' ? 'failed' : ''}`} key={r.connectionId}>
                  <div style={{ flex: 1 }}>
                    <div className="name">{r.connectionName}</div>
                    {r.error
                      ? <div className="line"><code>{r.error}</code></div>
                      : r.status === 'preview'
                        ? (
                          <pre className="sl-raw" style={{ maxHeight: 220, marginTop: 6, fontSize: 11 }}>
                            {JSON.stringify(r.response, null, 2)}
                          </pre>
                        )
                        : <div className="line">sent</div>}
                  </div>
                  <span className={`fs-pill ${STATUS_PILL[r.status] || STATUS_PILL.never}`}>
                    {r.status === 'preview' ? 'not sent' : STATUS_WORD[r.status] || r.status}
                  </span>
                </div>
              ))}
            </>
          ) : (
            <>
              <p className="sl-prose" style={{ marginTop: 0 }}>
                {bulk
                  ? <>Sends <b>{bulk.submissionIds.length} submissions</b> to the connections you tick, as their answers are now. Nothing is pre-ticked for a batch — say where they should go.</>
                  : <>Sends the answers <b>as they are now</b>{editedAgo ? ` — including your edit ${editedAgo}` : ''}. Failed connections are ticked; the ones that already landed are not.</>}
              </p>

              {connections.length === 0 && (
                <div className="fc-note">This form has no connections, so there is nowhere to send it.</div>
              )}

              {connections.map((c) => (
                <label className={`sl-conn ${c.status === 'failed' ? 'failed' : ''}`} key={c.id}>
                  <input
                    type="checkbox"
                    checked={picked.has(c.id)}
                    onChange={(e) => {
                      const next = new Set(picked);
                      if (e.target.checked) next.add(c.id); else next.delete(c.id);
                      setPicked(next);
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div className="name">{c.name}</div>
                    <div className="line">
                      {c.status === 'never'
                        ? 'never sent — this connection was added after the submission, or was off at the time'
                        : (
                          <>
                            {STATUS_WORD[c.status]} {c.lastAttemptAt ? formatFull(c.lastAttemptAt) : ''}
                            {c.attempts > 1 ? ` · attempt ${c.attempts}` : ''}
                            {c.error ? <> · <code>{c.error}</code></> : ''}
                          </>
                        )}
                    </div>
                    {/* A config edited since the failure is usually the reason a
                        re-send will now work. */}
                    {c.configChangedAt && c.status === 'failed' && (
                      <div className="line">Connection config changed {formatFull(c.configChangedAt)}</div>
                    )}
                    {c.status === 'success' && (
                      <div className="warn">Ticking this sends a second copy — the destination may create a duplicate.</div>
                    )}
                    {c.dryRun && (
                      <div className="warn">This connection is in test mode, so nothing will actually leave.</div>
                    )}
                  </div>
                  <span className={`fs-pill ${STATUS_PILL[c.status] || STATUS_PILL.never}`}>
                    {STATUS_WORD[c.status] || c.status}
                  </span>
                </label>
              ))}

              {!bulk && (
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 12 }}>
                  <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
                  Dry run — build the payload and show it, send nothing
                </label>
              )}
            </>
          )}
        </div>

        <div className="sl-pop-foot">
          {queued !== null ? (
            <div className="sl-prose">
              <b>{queued} submission{queued === 1 ? '' : 's'} queued.</b> They are being sent in the background — the
              Deliveries column updates as each one finishes, so refresh in a moment to see where they got to.
            </div>
          ) : results ? (
            <>
              <span style={{ flex: 1 }} />
              {results.some((r) => r.status === 'failed') && (
                <button className="fs-btn neutral sm" onClick={() => setResults(null)}>Back</button>
              )}
              <button className="fs-btn primary sm" onClick={onClose}>Close</button>
            </>
          ) : (
            <>
              <span className="sl-fp-word">
                {chosen.length} connection{chosen.length === 1 ? '' : 's'} selected
              </span>
              <span style={{ flex: 1 }} />
              <button className="fs-btn neutral sm" onClick={onClose}>Cancel</button>
              <button className="fs-btn primary sm" disabled={!chosen.length || sending} onClick={send}>
                {sending ? 'Sending…' : dryRun ? 'Show the payload' : 'Send now'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
